import { doc, updateDoc as fsUpdateDoc, setDoc as fsSetDoc, addDoc as fsAddDoc, deleteDoc as fsDeleteDoc, serverTimestamp, onSnapshot as fsOnSnapshot, getDocs as fsGetDocs, writeBatch } from 'firebase/firestore';
import { db, auth } from './firebase';
import { appId, MOBILE_SECTION_IDS } from './constants';

export function reconcileSectionOrder(saved) {
  if (!Array.isArray(saved) || saved.length === 0) {
    return [...MOBILE_SECTION_IDS];
  }
  const result = [...saved];
  for (const id of MOBILE_SECTION_IDS) {
    if (!result.includes(id)) {
      result.push(id);
    }
  }
  return result;
}

window.NYTM_METRICS = window.NYTM_METRICS || { 
  reads: 0, 
  writes: 0,
  getReport: function() {
    return `Firestore Usage Report:\nReads: ${this.reads}\nWrites: ${this.writes}`;
  }
};
// --- CIRCUIT BREAKER ---
let writeCount = 0;
let lastReset = Date.now();
let isCircuitBreakerTripped = false;
const CIRCUIT_BREAKER_WRITE_LIMIT = 400; // max writes per window
const CIRCUIT_BREAKER_TIME_WINDOW_MS = 10000; // 10 seconds

export const checkCircuitBreaker = () => {
  if (isCircuitBreakerTripped) {
    throw new Error('🚨 EMERGENCY: Circuit Breaker remains tripped. Writes are halted.');
  }
  
  window.NYTM_METRICS.writes++;
  const now = Date.now();
  if (now - lastReset > CIRCUIT_BREAKER_TIME_WINDOW_MS) {
    writeCount = 0;
    lastReset = now;
  }
  writeCount++;
  
  if (writeCount > CIRCUIT_BREAKER_WRITE_LIMIT) {
    isCircuitBreakerTripped = true;
    const msg = `🚨 EMERGENCY: Circuit Breaker Tripped! Runaway loop detected (${writeCount} writes in ${CIRCUIT_BREAKER_TIME_WINDOW_MS/1000}s). Application halted to prevent billing spike. Refresh the page to reset.`;
    console.error(msg);
    alert(msg);
    throw new Error(msg); // Halts execution of the calling function
  }
};

export const safeSetDoc = (docRef, data, options) => {
  checkCircuitBreaker();
  return fsSetDoc(docRef, data, options);
};

export const safeUpdateDoc = (docRef, data) => {
  checkCircuitBreaker();
  return fsUpdateDoc(docRef, data);
};

export const safeAddDoc = (colRef, data) => {
  checkCircuitBreaker();
  return fsAddDoc(colRef, data);
};

export const safeDeleteDoc = (docRef) => {
  checkCircuitBreaker();
  return fsDeleteDoc(docRef);
};

export const safeWriteBatch = (dbInstance) => {
  const batch = writeBatch(dbInstance);
  return {
    set: (docRef, data, options) => {
      checkCircuitBreaker();
      return batch.set(docRef, data, options);
    },
    update: (docRef, data) => {
      checkCircuitBreaker();
      return batch.update(docRef, data);
    },
    delete: (docRef) => {
      checkCircuitBreaker();
      return batch.delete(docRef);
    },
    commit: () => {
      return batch.commit();
    }
  };
};

export const safeOnSnapshot = (query, callback, errorCallback) => {
  return fsOnSnapshot(query, (snapshot) => {
    // Determine if it's a QuerySnapshot (has docs) or DocumentSnapshot
    let readCount = 0;
    if (snapshot.docs) {
      // It's a query snapshot
      readCount = snapshot.docs.length;
    } else {
      // It's a document snapshot
      readCount = snapshot.exists() ? 1 : 0;
    }
    
    // Also account for documentChanges() which trigger reads if they are added/modified
    // but the initial load returns all docs, so using docs.length is standard for the payload size.
    // NOTE: In Firestore, if a snapshot updates 1 document, it charges 1 read.
    // If it's not the first load, snapshot.docChanges().length is a better metric for incremental reads.
    if (snapshot.docChanges) {
      const changes = snapshot.docChanges();
      if (!snapshot.metadata.fromCache) {
         // If it's the very first load, docChanges() equals docs.length.
         // If it's a subsequent update, docChanges() only has the changed docs.
         window.NYTM_METRICS.reads += changes.length;
      }
    } else {
      if (!snapshot.metadata?.fromCache) {
         window.NYTM_METRICS.reads += readCount;
      }
    }
    
    callback(snapshot);
  }, errorCallback);
};

export const safeGetDocs = async (query) => {
  const snapshot = await fsGetDocs(query);
  if (!snapshot.metadata?.fromCache) {
     window.NYTM_METRICS.reads += snapshot.docs.length;
  }
  return snapshot;
};
// -----------------------
export const compressImage = (file, maxWidth = 1600, maxHeight = 1600, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], `thumb_${file.name}`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            reject(new Error('Canvas toBlob failed'));
          }
        }, 'image/jpeg', quality);
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};
// -----------------------

export const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
    return false;
  }
  
  if (Array.isArray(obj1)) {
    if (!Array.isArray(obj2)) return false;
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }
  
  if (obj1.isEqual && typeof obj1.isEqual === 'function' && obj2.isEqual && typeof obj2.isEqual === 'function') {
    return obj1.isEqual(obj2);
  }

  // Handle Firestore Timestamps if they somehow don't have isEqual in this context
  if (obj1.seconds !== undefined && obj1.nanoseconds !== undefined && 
      obj2.seconds !== undefined && obj2.nanoseconds !== undefined) {
    if (obj1.seconds !== obj2.seconds || obj1.nanoseconds !== obj2.nanoseconds) return false;
    // Timestamps might have no other enumerable keys, return early
    if (Object.keys(obj1).length === 0 || (Object.keys(obj1).length === 2 && 'seconds' in obj1 && 'nanoseconds' in obj1)) return true;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  
  for (let i = 0; i < keys1.length; i++) {
    const key = keys1[i];
    if (!Object.prototype.hasOwnProperty.call(obj2, key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }
  return true;
};

// Helper to linkify URLs in text
export const linkify = (text) => {
  if (!text) return '';
  // Split by HTML tags to avoid mangling them
  const parts = text.split(/(<[^>]+>)/g);
  return parts
    .map((part) => {
      if (part.startsWith('<')) return part; // Return tags as-is
      // Linkify only plain text parts
      const urlRegex = /((?:https?:\/\/|www\.)[^\s]+|(?:\b[a-z0-9]+\.[a-z]{2,}[^\s]*\b))/gi;
      return part.replace(urlRegex, (url) => {
        let href = url;
        if (!/^https?:\/\//i.test(url)) {
          href = 'https://' + url;
        }
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline break-all">${url}</a>`;
      });
    })
    .join('');
};

export const getTodayDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00`;
};

export const updateChannelActivity = async (channelId, userId) => {
  if (!channelId) return;
  // Ignore virtual/personal channels that aren't in the main channels collection
  if (channelId === 'general_chat_global' || channelId.startsWith('personal-')) return;
  try {
    const channelRef = doc(db, 'artifacts', appId, 'public', 'data', 'channels', channelId);
    await safeUpdateDoc(channelRef, {
      lastActivity: serverTimestamp(),
      lastActivityBy: userId,
    });
  } catch (e) {
    // If it's a "No document to update" error, it's likely a virtual channel we missed in the filter
    if (e.code === 'not-found') return;
    console.error('Error updating channel activity:', e);
  }
};

export const normalizeDate = (dateVal) => {
  if (!dateVal) return '';
  if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
    return dateVal;
  }
  try {
    let d;
    if (dateVal.seconds) d = new Date(dateVal.seconds * 1000);
    else d = new Date(dateVal);

    if (isNaN(d.getTime())) return '';
    // Use UTC date to avoid timezone shifts to the next day when rendering absolute timestamps
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    return '';
  }
};

export const getAbsoluteTimeMs = (localDateStr, localTimeStr, tzName) => {
  if (!localDateStr) return 0;
  const time = localTimeStr || '00:00:00';
  const dtStr = `${localDateStr}T${time}`;

  if (!tzName || tzName === 'home') {
    return new Date(dtStr).getTime();
  }

  try {
    const utcMs = new Date(`${dtStr}Z`).getTime();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tzName,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(new Date(utcMs));
    const p = {};
    parts.forEach(part => p[part.type] = part.value);
    
    const hourFixed = p.hour === '24' ? '00' : p.hour;
    const localMs = new Date(`${p.year}-${p.month}-${p.day}T${hourFixed}:${p.minute}:${p.second}Z`).getTime();
    
    const offsetMs = localMs - utcMs;
    return utcMs - offsetMs;
  } catch (e) {
    return new Date(dtStr).getTime();
  }
};

export const getUSHolidays = (year) => {
  const holidays = [
    { name: "New Year's Day", date: `${year}-01-01` },
    { name: 'Juneteenth', date: `${year}-06-19` },
    { name: 'Independence Day', date: `${year}-07-04` },
    { name: 'Veterans Day', date: `${year}-11-11` },
    { name: 'Christmas', date: `${year}-12-25` },
    { name: "New Year's Eve", date: `${year}-12-31` },
  ];
  const getNthDay = (y, m, dow, n) => {
    const d = new Date(y, m, 1);
    d.setDate(d.getDate() + ((dow - 1 - d.getDay() + 7) % 7) + (n - 1) * 7);
    return d.toISOString().split('T')[0];
  };
  const getLastDay = (y, m, dow) => {
    const d = new Date(y, m + 1, 0);
    d.setDate(d.getDate() - ((d.getDay() - dow + 7) % 7));
    return d.toISOString().split('T')[0];
  };
  holidays.push({ name: 'MLK Jr. Day', date: getNthDay(year, 0, 1, 3) });
  holidays.push({ name: "Presidents' Day", date: getNthDay(year, 1, 1, 3) });
  holidays.push({ name: 'Memorial Day', date: getLastDay(year, 4, 1) });
  holidays.push({ name: 'Labor Day', date: getNthDay(year, 8, 1, 1) });
  holidays.push({ name: 'Columbus Day', date: getNthDay(year, 9, 1, 2) });
  holidays.push({ name: 'Thanksgiving', date: getNthDay(year, 10, 4, 4) });
  return holidays.map((h) => ({
    id: `holiday-${h.date}-${h.name.replace(/\s+/g, '-')}`,
    type: 'holiday',
    title: h.name,
    startDate: h.date,
    endDate: h.date,
    isAllDay: true,
    isReadOnly: true,
  }));
};

export const getItemColor = (item) => {
  if (item.type === 'project') return '#000000';
  if (item.type === 'task') return '#22c55e';
  if (item.type === 'shopping') return '#f43f5e'; // Rose 500
  if (item.type === 'wanttogo') return '#10b981'; // Emerald 500
  if (item.type === 'event') return '#71717a'; // Standard Gray (Zinc 600)

  if (item.type === 'restaurant') return '#f97316'; // Orange 500
  if (item.type === 'drinks') return '#a855f7'; // Purple 500
  if (item.type === 'coffee') return '#b45309'; // Amber 700
  if (item.type === 'sight') return '#14b8a6'; // Teal 500
  if (item.type === 'concert') return '#ec4899'; // Pink 500
  if (item.type === 'musicvenue') return '#6366f1'; // Indigo 500
  if (item.type === 'birthday') return '#ec4899'; // Pink 500
  if (item.type === 'alias') return '#18181b'; // Zinc 900
  if (item.type === 'tentative') return '#4f46e5';
  if (item.type === 'hotel') return '#6366f1';
  if (item.type === 'trip') return '#b91c1c'; // Slightly less dark red (Red 700)
  return item.color || '#7c3aed';
};

export const getTimezoneOffsetHours = (timezone, dateStr) => {
  if (!timezone) return 0;
  try {
    const date = new Date(`${dateStr}T12:00:00Z`);
    const tzParts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric',
      hour12: false
    }).formatToParts(date);
    
    let year, month, day, hour, minute, second;
    for (const part of tzParts) {
      if (part.type === 'year') year = part.value;
      if (part.type === 'month') month = part.value;
      if (part.type === 'day') day = part.value;
      if (part.type === 'hour') hour = part.value;
      if (part.type === 'minute') minute = part.value;
      if (part.type === 'second') second = part.value;
    }
    // Parse hours specifically to handle 24:00 (which Intl sometimes returns for midnight)
    if (hour === '24') hour = '00';
    
    const localDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    return (localDate.getTime() - date.getTime()) / (1000 * 3600);
  } catch (e) {
    return 0;
  }
};

export const getWeatherEmoji = (code) => {
  if (code === 0) return '☀️';
  if (code === 1 || code === 2) return '⛅';
  if (code === 3) return '☁️';
  if (code >= 45 && code <= 48) return '🌫️';
  if (code >= 51 && code <= 67) return '🌧️';
  if (code >= 71 && code <= 77) return '❄️';
  if (code >= 80 && code <= 82) return '🌦️';
  if (code >= 85 && code <= 86) return '🌨️';
  if (code >= 95) return '⛈️';
  return '🌡️';
};

export const getActiveLocationsForDate = (dateStr, dateObj, trips, events, homeCity) => {
  const locs = [];
  
  // Is dateObj "today" in local time?
  const now = new Date();
  const isToday = dateObj.getDate() === now.getDate() && dateObj.getMonth() === now.getMonth() && dateObj.getFullYear() === now.getFullYear();
  
  // Find events for this day
  const dayEvents = (events || []).filter(e => 
    e.startDate === dateStr && 
    ['event', 'tentative', 'concert', 'restaurant', 'sight'].includes(e.type) && 
    ((e.lat && e.lng) || (e.addressLat && e.addressLng))
  );
  
  let activeEventLoc = null;
  
  if (isToday) {
    // Check if we are within 1 hour before/after any event
    const currentMs = now.getTime();
    for (const e of dayEvents) {
      if (e.startTime) {
        // e.startTime is "HH:mm"
        const [hours, minutes] = e.startTime.split(':').map(Number);
        const eventStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);
        let eventEndMs = eventStart.getTime() + (e.duration ? parseInt(e.duration, 10) * 60000 : 60 * 60000); // default 1 hr duration
        
        // 1 hour before, 1 hour after
        const windowStartMs = eventStart.getTime() - 60 * 60000;
        const windowEndMs = eventEndMs + 60 * 60000;
        
        if (currentMs >= windowStartMs && currentMs <= windowEndMs) {
          activeEventLoc = { 
            lat: e.addressLat || e.lat, 
            lng: e.addressLng || e.lng, 
            date: dateObj, 
            type: e.type, 
            id: e.id,
            name: e.venueName ? `${e.title} @ ${e.venueName}` : e.title,
            address: e.address,
            city: e.city
          };
          break; // Use the first active event
        }
      }
    }
  }
  
  if (activeEventLoc) {
    // We are within an event override window! Return ONLY this location.
    return [activeEventLoc];
  }
  
  // If no active event override, return ALL locations for the day (base location + all events)
  const outTrips = trips.filter(t => t.endDate === dateStr);
  const stayTrips = trips.filter(t => t.startDate < dateStr && t.endDate > dateStr);
  const inTrips = trips.filter(t => t.startDate === dateStr);
  
  if (homeCity && stayTrips.length === 0) {
    locs.push({ 
      lat: homeCity.lat, 
      lng: homeCity.lng, 
      date: dateObj, 
      type: 'home',
      name: homeCity.name || 'Home',
      address: homeCity.address,
      city: homeCity.city || homeCity.name
    });
  }
  
  const addTripLoc = (t) => {
    const hotel = t.hotels?.find(h => h.startDate <= dateStr && h.endDate >= dateStr);
    if (hotel && hotel.lat && hotel.lng) {
      locs.push({ lat: hotel.lat, lng: hotel.lng, date: dateObj, type: 'hotel', id: hotel.id, name: hotel.name, address: hotel.address, city: t.city });
    } else {
      locs.push({ lat: t.lat, lng: t.lng, date: dateObj, type: 'trip', id: t.id, name: t.city || t.name, city: t.city });
    }
  };
  
  outTrips.forEach(addTripLoc);
  stayTrips.forEach(addTripLoc);
  inTrips.forEach(addTripLoc);
  


  // Remove duplicates based on lat/lng to optimize distance calculations
  const uniqueLocs = [];
  const seen = new Set();
  for (const loc of locs) {
    const key = `${loc.lat},${loc.lng}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueLocs.push(loc);
    }
  }
  
  return uniqueLocs;
};

export const checkPlacesRateLimit = () => {
  try {
    const limit = 500;
    const today = new Date().toISOString().split('T')[0];
    const data = JSON.parse(localStorage.getItem('places_api_usage') || '{}');
    if (data.date !== today) {
      localStorage.setItem('places_api_usage', JSON.stringify({ date: today, count: 1 }));
      return true;
    }
    if (data.count >= limit) {
      console.error(`[RateLimit] Daily Places API limit (${limit}) reached. Failsafe triggered.`);
      return false;
    }
    localStorage.setItem('places_api_usage', JSON.stringify({ date: today, count: data.count + 1 }));
    return true;
  } catch (e) {
    return true; // Fallback to allowing if localStorage fails
  }
};

export const getTypeColor = (type) => {
  switch (type) {
    case 'restaurant': return '#f97316';
    case 'drinks': return '#a855f7';
    case 'coffee': return '#a16207';
    case 'sight': return '#0ea5e9';
    case 'concert': return '#ef4444';
    case 'shopping': return '#f43f5e';
    case 'wanttogo': return '#10b981';
    default: return '#10b981';
  }
};

export const getMarkerGradient = (multiTypes) => {
  if (!multiTypes || multiTypes.length === 0) return '';
  if (multiTypes.length === 1) return getTypeColor(multiTypes[0]);
  const total = multiTypes.length;
  const slices = multiTypes.map((t, idx) => {
    const color = getTypeColor(t);
    const startPct = (idx / total) * 100;
    const endPct = ((idx + 1) / total) * 100;
    return `${color} ${startPct}% ${endPct}%`;
  });
  return `conic-gradient(${slices.join(', ')})`;
};

export const authedFetch = async (url, options = {}) => {
  if (!auth.currentUser) {
    throw new Error('User must be authenticated to make this request.');
  }
  const token = await auth.currentUser.getIdToken();
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`
  };
  return fetch(url, { ...options, headers });
};
